import React, { useEffect, useState } from 'react';
import { Table, Tag, Button, Alert, Tooltip, Spin, message } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import axios from 'axios';
import AuthService from '../../utils/auth';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';
const FRONTEND_URL = window.location.origin;

const StepPreview = ({ tourists, tour, tourNotice, completeness, onRefresh }) => {
  const [generating, setGenerating] = useState(false);
  const allTouristDetails = completeness?.tourists || [];
  const isDaytour = tour?.tourType === 'daytour';
  // 一日游排除已取消和已退款的游客
  const touristDetails = isDaytour
    ? allTouristDetails.filter(t => t.orderStatus !== 'cancelled' && t.orderStatus !== 'refunded')
    : allTouristDetails;
  const tourLevel = completeness?.tour;
  const stats = tourLevel?.touristStats;

  // 进入预览步骤时，自动为未生成链接的游客生成链接
  useEffect(() => {
    const hasUngenerated = touristDetails.some(t => !t.noticeLink);
    if (hasUngenerated && tour?.id) {
      generateLinks();
    }
  }, [tour?.id]);

  const generateLinks = async () => {
    try {
      setGenerating(true);
      await axios.post(
        `${API_BASE}/tour-notices/tour/${tour.id}/generate-all-links`,
        {},
        { headers: AuthService.getAuthHeaders() }
      );
      await onRefresh();
    } catch (err) {
      message.error('生成通知书链接失败: ' + (err.response?.data?.message || err.message));
    } finally {
      setGenerating(false);
    }
  };

  // Completeness check alerts
  const alerts = [];
  if (!tourLevel?.hasNotice) alerts.push({ type: 'error', msg: '通知书尚未创建' });
  if (!tourLevel?.hasDestination) alerts.push({ type: 'warning', msg: '未设置目的地' });

  if (isDaytour) {
    if (!tourLevel?.hasGuide) alerts.push({ type: 'warning', msg: '未分配导游' });
  } else {
    if (!tourLevel?.hasFlightInfo) alerts.push({ type: 'warning', msg: '未配置航班信息' });
    if (stats) {
      if (stats.hotelAssigned < stats.total) alerts.push({ type: 'warning', msg: `${stats.total - stats.hotelAssigned} 位游客未分配酒店` });
      if (stats.flightTicket < stats.total) alerts.push({ type: 'warning', msg: `${stats.total - stats.flightTicket} 位游客未上传机票` });
      if (stats.passportReady < stats.total) alerts.push({ type: 'info', msg: `${stats.total - stats.passportReady} 位游客护照未验证` });
      // 中国团附加证件：中国护照要身份证、台湾护照要台胞证
      if (stats.chinaDocReady != null && stats.chinaDocReady < stats.total) {
        alerts.push({ type: 'warning', msg: `${stats.total - stats.chinaDocReady} 位游客未上传身份证/台胞证` });
      }
    }
  }

  // 一日游列
  const daytourColumns = [
    {
      title: '游客姓名',
      dataIndex: 'touristName',
      key: 'touristName',
      width: 120,
    },
    {
      title: '上车点',
      key: 'pickupPoint',
      width: 150,
      render: (_, record) => {
        const tourist = tourists.find(t => t.id === record.id);
        return tourist?.pickupPoint || '-';
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => {
        if (!record.noticeLink) {
          return <Tag color="default">未生成</Tag>;
        }
        return (
          <Tooltip title="在新窗口打开通知书">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => window.open(`${FRONTEND_URL}/notice/${record.noticeLink}`, '_blank')}
            >
              预览
            </Button>
          </Tooltip>
        );
      },
    },
  ];

  // 出境游列
  const standardColumns = [
    {
      title: '护照姓名',
      dataIndex: 'passportName',
      key: 'passportName',
      width: 150,
      render: (text) => text || '-',
    },
    {
      title: '酒店',
      key: 'hotel',
      width: 100,
      render: (_, record) => record.hotelAssigned
        ? <Tag color="success">已分配</Tag>
        : <Tag color="warning">未分配</Tag>,
    },
    {
      title: '机票',
      key: 'flightTicket',
      width: 100,
      render: (_, record) => record.flightTicket
        ? <Tag color="success">已上传</Tag>
        : <Tag color="warning">未上传</Tag>,
    },
    {
      title: '护照',
      key: 'passport',
      width: 100,
      render: (_, record) => record.passportReady
        ? <Tag color="success">已验证</Tag>
        : <Tag color="default">未验证</Tag>,
    },
    {
      // 中国团：中国护照需身份证、台湾护照需台胞证；不需要的显示 -
      title: '身份证/台胞证',
      key: 'chinaDoc',
      width: 120,
      render: (_, record) => {
        if (record.chinaDocReady && !record.chinaDocLabel) return <span style={{ color: '#bbb' }}>-</span>;
        if (record.chinaDocReady) return <Tag color="success">已上传</Tag>;
        return <Tag color="error">缺{record.chinaDocLabel || '证件'}</Tag>;
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => {
        if (!record.noticeLink) {
          return <Tag color="default">未生成</Tag>;
        }
        return (
          <Tooltip title="在新窗口打开通知书">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => window.open(`${FRONTEND_URL}/notice/${record.noticeLink}`, '_blank')}
            >
              预览
            </Button>
          </Tooltip>
        );
      },
    },
  ];

  const columns = isDaytour ? daytourColumns : standardColumns;

  if (generating) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin tip="正在生成通知书链接..." />
      </div>
    );
  }

  return (
    <div>
      {alerts.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {alerts.map((a, i) => (
            <Alert key={i} type={a.type} message={a.msg} showIcon style={{ marginBottom: 8 }} />
          ))}
        </div>
      )}

      <Table
        columns={columns}
        dataSource={touristDetails}
        rowKey="id"
        size="small"
        pagination={false}
      />
    </div>
  );
};

export default StepPreview;
